import {
    PocComponentFooter,
    PocComponentHeader,
    PocProgressSpinner,
    PocTypography,
} from 'commons/components/components-wrapper';
import useCheckUnsavedChanges from 'core/custom-hooks/use-check-unsaved-changes.hook';
import { useTranslation } from 'core/i18n/i18n';
import React, { cloneElement, useCallback, useEffect, useRef, useState } from 'react';
import { TooltipedIconComponent } from '../tooltiped-icon';
import { SidesheetDrawerStyled } from './sidesheet-drawer.style';
import { useCloseOnClickOutside } from './use-close-on-click-outside.hook';

const TRANSPARENT_BACKDROP_PROPS = { style: { backgroundColor: 'transparent' } };

export interface SidesheetDrawerComponentProps extends React.PropsWithChildren {
    show: boolean;
    headerTitle: string;
    loading?: boolean;
    headerOptions?: React.ReactElement[];
    footerOptions?: React.ReactElement;
    closeOnClickOutside?: boolean;
    showFooter?: boolean;
    dataProtected?: boolean;
    avoidOnClickOutside?: (target: HTMLElement | null) => boolean;
    onClose: VoidFunction;
    onGoBackHandler?: VoidFunction;
}

export const SidesheetDrawerComponent: React.FC<SidesheetDrawerComponentProps> = ({
    show,
    headerTitle,
    loading = false,
    headerOptions,
    footerOptions,
    closeOnClickOutside = false,
    showFooter = false,
    dataProtected = false,
    avoidOnClickOutside,
    onClose,
    onGoBackHandler,
    children,
}) => {
    const { t } = useTranslation();
    const panelRef = useRef<HTMLDivElement>(null);
    // Stay mounted while a close is pending so an external `show → false` still routes through the
    // unsaved-changes guard before unmounting (v6 parity — the old SidesheetComponent guarded it).
    const [rendered, setRendered] = useState<boolean>(show);

    const handleClose = useCallback(() => {
        setRendered(false);
        onClose();
    }, [onClose]);

    const { setIsUnsavedChanges, showUnsavedChangesIfNeededOrClose } = useCheckUnsavedChanges(handleClose);
    const requestClose = useCallback(() => showUnsavedChangesIfNeededOrClose(), [showUnsavedChangesIfNeededOrClose]);

    const latestRef = useRef({ requestClose, rendered });
    latestRef.current = { requestClose, rendered };

    useEffect(() => {
        setIsUnsavedChanges(dataProtected);
    }, [dataProtected, setIsUnsavedChanges]);

    useEffect(() => {
        if (show) {
            setRendered(true);
        } else if (latestRef.current.rendered) {
            latestRef.current.requestClose();
        }
    }, [show]);

    useCloseOnClickOutside({
        active: rendered,
        enabled: closeOnClickOutside,
        panelRef,
        avoid: avoidOnClickOutside,
        onCloseRequest: requestClose,
    });

    if (!rendered) {
        return null;
    }

    return (
        <SidesheetDrawerStyled
            visible={rendered}
            placement="right"
            mode="over"
            controlled
            noAutoFocus
            disableEscapeKeydown
            backdropProps={TRANSPARENT_BACKDROP_PROPS}
        >
            <div slot="panel" ref={panelRef}>
                {loading ? (
                    <PocProgressSpinner variant="primary" size="large" />
                ) : (
                    <>
                        <PocComponentHeader separator>
                            {onGoBackHandler ? (
                                <div slot="prefix">
                                    <TooltipedIconComponent
                                        dataTestId="sidesheet-drawer-back"
                                        icon="arrow_leftwards"
                                        tooltipText={t('commons.components.icon.chevron-back.tooltip')}
                                        onClick={onGoBackHandler}
                                    />
                                    <PocTypography variant="button">{t(headerTitle)}</PocTypography>
                                </div>
                            ) : (
                                <PocTypography variant="button">{t(headerTitle)}</PocTypography>
                            )}
                            <div slot="suffix">
                                {headerOptions?.map((comp) =>
                                    cloneElement(comp, {
                                        key: comp.key ?? (comp.props.id ? `row-option-${comp.props.id}` : undefined),
                                    })
                                )}
                                <TooltipedIconComponent
                                    icon="clear"
                                    tooltipText={t('commons.components.icon.close.tooltip')}
                                    onClick={requestClose}
                                />
                            </div>
                        </PocComponentHeader>
                        <div className="sidesheet-drawer__content">{children}</div>
                        {showFooter && footerOptions ? (
                            <PocComponentFooter separator>{footerOptions}</PocComponentFooter>
                        ) : undefined}
                    </>
                )}
            </div>
        </SidesheetDrawerStyled>
    );
};
